import { useState, useRef } from 'react';
import type { TournamentJSON } from '@/lib/tournamentTransfer';

interface ImportTournamentModalProps {
  onConfirm: (data: TournamentJSON, name: string, startDate: string, endDate: string) => Promise<void>;
  onCancel: () => void;
}

export default function ImportTournamentModal({ onConfirm, onCancel }: ImportTournamentModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<TournamentJSON | null>(null);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleFile = (file: File) => {
    setParseError(null);
    setSaveError(null);
    setParsed(null);
    setFileName(file.name);

    if (!file.name.endsWith('.json')) {
      setParseError('Please select a .json file exported from this app.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string) as TournamentJSON;
        if (json.version !== '1.0' || !json.tournament || !Array.isArray(json.teams) || !Array.isArray(json.matches)) {
          setParseError('This file does not look like a valid tournament export.');
          return;
        }
        setParsed(json);
        setName(json.tournament.name);
        setStartDate(json.tournament.startDate);
        setEndDate(json.tournament.endDate);
      } catch {
        setParseError('Could not read file. Make sure it is a valid JSON export.');
      }
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!parsed) return;
    const trimmed = name.trim();
    if (!trimmed) { setSaveError('Tournament name is required.'); return; }
    if (!startDate || !endDate) { setSaveError('Start and end dates are required.'); return; }
    if (endDate < startDate) { setSaveError('End date must be on or after start date.'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      await onConfirm(parsed, trimmed, startDate, endDate);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Import failed. Please try again.');
      setSaving(false);
    }
  };

  const totalRallies = parsed?.matches.reduce((sum, m) =>
    sum + m.sets.reduce((ss, s) => ss + s.rallies.length, 0), 0) ?? 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl max-h-[90vh] flex flex-col">
        <div className="px-5 pt-5 pb-4 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">Import Tournament</h2>
          <p className="text-xs text-text-muted mt-0.5">
            Load a tournament from a .json export file. Teams are matched by name to existing records.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
          {/* File drop zone */}
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${
              parsed ? 'border-success/50 bg-success/5' : 'border-border hover:border-primary/50 hover:bg-primary/5'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            {parsed ? (
              <div className="space-y-1">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-success">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <p className="text-xs font-bold text-success truncate">{fileName}</p>
                <p className="text-[10px] text-text-muted">Click to change file</p>
              </div>
            ) : (
              <div className="space-y-1">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-text-muted">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
                <p className="text-xs font-semibold text-text-secondary">Drop JSON file here</p>
                <p className="text-[10px] text-text-muted">or click to browse</p>
              </div>
            )}
          </div>

          {parseError && (
            <p className="text-xs text-error font-semibold">{parseError}</p>
          )}

          {/* Preview */}
          {parsed && (
            <div className="rounded-xl bg-surface-raised border border-border p-3 space-y-2 text-xs">
              <p className="font-bold text-text-secondary uppercase tracking-wider text-[10px]">Preview</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-text-primary">{parsed.teams.length}</p>
                  <p className="text-text-muted">Teams</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-text-primary">{parsed.matches.length}</p>
                  <p className="text-text-muted">Matches</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-text-primary">{totalRallies.toLocaleString()}</p>
                  <p className="text-text-muted">Rallies</p>
                </div>
              </div>
              <p className="text-[10px] text-text-muted border-t border-border pt-2">
                Exported: {new Date(parsed.exportedAt).toLocaleString()}
              </p>
            </div>
          )}

          {/* Form fields — shown once file is parsed */}
          {parsed && (
            <>
              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1.5">Tournament Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="input-field text-sm"
                  placeholder="Tournament name"
                  required
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1.5">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="input-field text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1.5">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="input-field text-sm"
                    required
                  />
                </div>
              </div>

              <p className="text-[11px] text-text-muted bg-surface-raised border border-border rounded-lg px-3 py-2">
                <strong className="text-text-secondary">Note:</strong> Teams are matched by name. Any team not found in the system will have its matches skipped. Ensure all teams exist before importing.
              </p>
            </>
          )}

          {saveError && (
            <p className="text-xs text-error font-semibold">{saveError}</p>
          )}
        </form>

        <div className="flex gap-3 px-5 pb-5 pt-2 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 py-3 rounded-xl font-semibold text-sm border border-border text-text-secondary hover:bg-surface-raised transition-all active:scale-[0.98] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form=""
            disabled={saving || !parsed}
            onClick={handleSubmit}
            className="flex-1 py-3 rounded-xl font-bold text-sm bg-primary text-white hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-50"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Importing…
              </span>
            ) : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}
